import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { useSession } from '@client/lib/useSession';
import { registerSchema, MIN_PASSWORD_LENGTH } from '@shared/schemas/auth';
import styles from './AuthForm.module.css';

/**
 * Public signup.
 *
 * WHAT THIS CREATES: a brand-new, empty business, owned by the person filling
 * the form in. It is NOT a way to join an existing one — there is no field here
 * for an organization, because the server generates the id and never reads one
 * from the body. Somebody joining a business that already exists arrives by an
 * invitation link instead (AcceptInvitePage).
 *
 * The copy says so out loud, because "create an account" on a login page
 * reasonably reads as "join this business" to somebody a crew lead told to sign
 * up, and they would otherwise end up alone in an empty organization wondering
 * where the work went.
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [organizationName, setOrganizationName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Whether this deployment is accepting signups.
   *
   * The login page already hides its link when registration is closed, so
   * nobody is *led* here — but a bookmark, a stale link or a typed URL still
   * arrives, and offering a form that can only end in a 403 is the dead end the
   * flag exists to avoid. `null` means "not asked yet", so the form is not
   * flashed on screen and then withdrawn.
   */
  const [isOpen, setIsOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    apiGet<{ enabled: boolean }>('/auth/registration')
      .then((result) => {
        if (!cancelled) setIsOpen(result.enabled === true);
      })
      // Fall back to SHOWING the form. The server is the authority and will
      // refuse if it is closed; assuming closed because a check failed would
      // turn a blip into a locked door.
      .catch(() => {
        if (!cancelled) setIsOpen(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;

    // Validate with the SAME schema the server validates with, so the rules
    // cannot drift apart and a rejection is explained before a round trip.
    const parsed = registerSchema.safeParse({
      organizationName,
      displayName,
      email,
      password,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the form and try again.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await apiPost('/auth/register', parsed.data);
      await refresh();
      // Not /dashboard: analytics for a business created ten seconds ago is an
      // empty chart, which reads as broken. /today is where the work is.
      navigate('/today');
    } catch (err) {
      if (err instanceof ApiClientError) {
        // The server's messages here are safe to show verbatim — "that email
        // already has an account", "registration is closed", "too many
        // attempts" are each actionable, and none of them is about a session.
        setError(err.message);
      } else {
        setError('Could not create the account. Try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const describedBy = error ? 'register-error' : undefined;

  if (isOpen === null) {
    return (
      <main className={styles.main} id="main">
        <div className={styles.card}>
          <p className={styles.status}>Loading…</p>
        </div>
      </main>
    );
  }

  if (!isOpen) {
    return (
      <main className={styles.main} id="main">
        <div className={styles.card}>
          <h1 className={styles.title}>Accounts are by invitation</h1>
          <p className={styles.subtitle}>
            This site is not accepting new signups. If you work here, ask the owner to send you an
            invitation link from the Team page.
          </p>
          <p className={styles.footer}>
            <Link to="/login" className={styles.footerLink}>
              Go to sign in
            </Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main} id="main">
      <div className={styles.card}>
        <h1 className={styles.title}>Create an account</h1>
        <p className={styles.subtitle}>
          This sets up a new business of your own. If you were asked to join an existing one, use
          the invitation link you were sent instead.
        </p>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="organizationName" className={styles.label}>
              Business name
            </label>
            <input
              id="organizationName"
              name="organizationName"
              type="text"
              autoComplete="organization"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-describedby={describedBy}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="displayName" className={styles.label}>
              Your name
            </label>
            <input
              id="displayName"
              name="displayName"
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-describedby={describedBy}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-describedby={describedBy}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-describedby={describedBy}
            />
            <p className={styles.hint}>At least {MIN_PASSWORD_LENGTH} characters.</p>
          </div>

          {error && (
            <p id="register-error" className={styles.error} role="alert" aria-live="polite">
              {error}
            </p>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" className={styles.footerLink}>
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
